import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import Icon from "@/components/Icon";
import AdminLayout from "@/components/AdminLayout";
import { DESTINATIONS } from "@/lib/qr";

export default function AdminQRCodes() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [dtype, setDtype] = useState("all");
  const [sort, setSort] = useState("created_desc");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/qrcodes", { params: { q: q || undefined, status, destination_type: dtype, sort, page, page_size: 25 } })
      .then(({ data }) => setData(data)).catch(() => {});
  }, [q, status, dtype, sort, page]);
  useEffect(() => { load(); }, [load]);

  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <AdminLayout title="QR Codes" subtitle="Every QR record across all customers">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Icon name="Search" className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="qr-search" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search by name or public ID" className="w-full rounded-xl border border-input bg-background pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40" />
        </div>
        <select data-testid="admin-qr-status" value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className="rounded-xl border border-input bg-background px-3 py-2 text-sm"><option value="all">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option><option value="draft">Draft</option><option value="paused">Paused</option><option value="archived">Archived</option></select>
        <select data-testid="admin-qr-type" value={dtype} onChange={(e) => { setPage(1); setDtype(e.target.value); }} className="rounded-xl border border-input bg-background px-3 py-2 text-sm"><option value="all">All types</option>{DESTINATIONS.filter((d) => !d.hidden).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select>
        <select data-testid="admin-qr-sort" value={sort} onChange={(e) => { setPage(1); setSort(e.target.value); }} className="rounded-xl border border-input bg-background px-3 py-2 text-sm"><option value="created_desc">Newest</option><option value="created_asc">Oldest</option><option value="name_asc">Name A-Z</option><option value="scans_desc">Most scans</option></select>
        <span className="text-xs text-muted-foreground ml-auto">{data?.total ?? 0} total</span>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="qrcodes-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground"><tr>
              {["Name", "Public ID", "Type", "Status", "Scans", "Workspace"].map((h) => <th key={h} className="text-left font-semibold px-4 py-3 whitespace-nowrap">{h}</th>)}
            </tr></thead>
            <tbody>
              {!data && <tr><td colSpan={6} className="px-4 py-10 text-center"><Icon name="Loader2" className="h-6 w-6 animate-spin text-primary mx-auto" /></td></tr>}
              {data && data.items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No QR codes found.</td></tr>}
              {data && data.items.map((qr) => (
                <tr key={qr.id} data-testid={`qr-row-${qr.id}`} className="border-t border-border">
                  <td className="px-4 py-3 font-medium">{qr.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{qr.public_id}</td>
                  <td className="px-4 py-3">{qr.destination_type}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${qr.status === "active" ? "bg-emerald-500/15 text-emerald-600" : "bg-muted text-muted-foreground"}`}>{qr.status || "active"}</span></td>
                  <td className="px-4 py-3">{qr.scan_count || 0}</td>
                  <td className="px-4 py-3 text-muted-foreground">{qr.company_name || qr.company_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data && pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-border px-3 py-1.5 text-sm disabled:opacity-40">Prev</button>
          <span className="text-sm text-muted-foreground">Page {page} of {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-border px-3 py-1.5 text-sm disabled:opacity-40">Next</button>
        </div>
      )}
    </AdminLayout>
  );
}
